import { useCallback, useEffect, useRef, useState } from "react";

import apiClient from "../../../api-client.js";
import { useAuth } from "../../../components/contexts/AuthContext.jsx";

const POLLING_INTERVAL_MS = 1000;
const MAX_POLLING_ATTEMPTS = 60;
// Timeout: if no progress in 60 seconds, consider campaign stalled
const CAMPAIGN_PROGRESS_TIMEOUT_MS = 60000;

const IDLE_PROGRESS = { total: 0, completed: 0, failed: 0, pending: 0, status: "idle" };

const normalizeTopic = (topic) => {
  if (!topic) return "";
  if (typeof topic === "string") return topic;
  if (typeof topic === "object") {
    if (typeof topic.title === "string") return topic.title;
    if (typeof topic.name === "string") return topic.name;
    if (typeof topic.topic === "string") return topic.topic;
    return "";
  }
  return String(topic);
};

/**
 * Batch (campaign) generation: briefing → proposed topics → base texts per piece.
 *
 * phase: "briefing" (not started) | "topics_loading" | "topics_review" | "generating" | "done"
 * Errors and results are reported through `notify(message, isError)`.
 */
export function useCampaignGeneration({ notify } = {}) {
  const { interceptorReady } = useAuth();
  const notifyRef = useRef(notify);
  notifyRef.current = notify;
  const report = useCallback((message, isError = false) => notifyRef.current?.(message, isError), []);

  const [started, setStarted] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [configId, setConfigId] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [batchId, setBatchId] = useState(null);
  const [expectedPieces, setExpectedPieces] = useState(0);
  const [progress, setProgress] = useState(IDLE_PROGRESS);
  const [topicsTaskId, setTopicsTaskId] = useState(null);
  const [topics, setTopics] = useState([]);
  const [isEditingTopics, setIsEditingTopics] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [pieceIds, setPieceIds] = useState([]);
  const [singleTaskId, setSingleTaskId] = useState(null);

  const topicsPollingRef = useRef(null);
  const campaignPollingRef = useRef(null);
  const lastProcessedCountRef = useRef(0);
  const lastProgressAtRef = useRef(0);

  // ── Topics polling ──
  useEffect(() => {
    if (!isGenerating || !topicsTaskId || !configId || !interceptorReady) return;
    let attempts = 0;
    let isCancelled = false;

    const stopWithError = (message) => {
      report(message, true);
      setIsGenerating(false);
      setTopicsTaskId(null);
      setStarted(false);
    };

    const pollTopicsTask = async () => {
      try {
        const response = await apiClient.get(`/content-generation/task-status/${topicsTaskId}/`);
        const { status, result, error } = response.data;
        if (isCancelled) return;

        if (status === "SUCCESS") {
          const normalizedTopics = (Array.isArray(result?.topics) ? result.topics : [])
            .map(normalizeTopic)
            .filter((topic) => topic.trim() !== "");
          if (!normalizedTopics.length) {
            stopWithError("No se generaron contenidos. Inténtalo de nuevo.");
            return;
          }
          setTopicsTaskId(null);
          setTopics(normalizedTopics);
          setIsEditingTopics(true);
          setProgress((prev) => ({
            ...prev,
            total: normalizedTopics.length,
            pending: normalizedTopics.length,
          }));
          return;
        }

        if (status === "FAILURE") {
          stopWithError(error || "Error al generar contenidos.");
          return;
        }

        attempts += 1;
        if (attempts < MAX_POLLING_ATTEMPTS) {
          topicsPollingRef.current = setTimeout(pollTopicsTask, POLLING_INTERVAL_MS);
        } else {
          stopWithError("La generación está tardando demasiado.");
        }
      } catch (pollError) {
        console.error("Error polling topics task:", pollError);
        attempts += 1;
        if (attempts < MAX_POLLING_ATTEMPTS) {
          topicsPollingRef.current = setTimeout(pollTopicsTask, POLLING_INTERVAL_MS);
        } else {
          stopWithError("Error al verificar la generación.");
        }
      }
    };

    pollTopicsTask();

    return () => {
      isCancelled = true;
      if (topicsPollingRef.current) {
        clearTimeout(topicsPollingRef.current);
        topicsPollingRef.current = null;
      }
    };
  }, [isGenerating, topicsTaskId, configId, interceptorReady, report]);

  // ── Single piece task polling ──
  // The panel follows the piece itself; this only reports a failed or stuck task
  // (e.g. no Celery worker consuming the queue), which would otherwise look like endless "composing".
  useEffect(() => {
    if (!singleTaskId || !interceptorReady) return;
    let attempts = 0;
    let timeoutId = null;
    let isCancelled = false;

    const poll = async () => {
      try {
        const { data } = await apiClient.get(`/content-generation/task-status/${singleTaskId}/`);
        if (isCancelled) return;
        if (data.status === "SUCCESS") {
          setSingleTaskId(null);
          return;
        }
        if (data.status === "FAILURE") {
          setSingleTaskId(null);
          report(data.error || "Error al generar el contenido.", true);
          return;
        }
      } catch (pollError) {
        console.error("Error polling piece task:", pollError);
      }
      attempts += 1;
      if (attempts < MAX_POLLING_ATTEMPTS * 2) {
        timeoutId = setTimeout(poll, POLLING_INTERVAL_MS);
      } else {
        setSingleTaskId(null);
        report("La generación está tardando demasiado. El contenido quedará en la lista cuando termine.", true);
      }
    };

    timeoutId = setTimeout(poll, 500);
    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [singleTaskId, interceptorReady, report]);

  // ── Batch progress polling ──
  useEffect(() => {
    if (!isGenerating || !batchId || !configId || !interceptorReady) return;
    lastProcessedCountRef.current = 0;
    lastProgressAtRef.current = Date.now();

    const stopPolling = () => {
      if (campaignPollingRef.current) {
        clearInterval(campaignPollingRef.current);
        campaignPollingRef.current = null;
      }
    };

    const pollCampaignProgress = async () => {
      try {
        const response = await apiClient.get(`/content-generation/batch/${batchId}/progress/`);
        const { total, completed, failed, pending, status } = response.data;
        const processedCount = (completed || 0) + (failed || 0);

        setProgress({ total, completed, failed, pending, status });

        if (processedCount > lastProcessedCountRef.current) {
          lastProcessedCountRef.current = processedCount;
          lastProgressAtRef.current = Date.now();
        }

        if (status === "completed" || status === "completed_with_errors") {
          stopPolling();
          setIsGenerating(false);
          if (status === "completed_with_errors") {
            report(`Campaña generada con ${failed || 0} errores. ${completed || 0} contenidos listos.`, true);
          } else {
            report("¡Contenidos generados correctamente!");
          }
          return;
        }

        if (Date.now() - lastProgressAtRef.current >= CAMPAIGN_PROGRESS_TIMEOUT_MS) {
          stopPolling();
          report("La campaña está tardando demasiado en generar contenidos.", true);
          setIsGenerating(false);
        }
      } catch (pollError) {
        console.error("Error polling campaign progress:", pollError);

        // Batch not found/expired: stop silently, the pieces are already saved.
        const errorMessage = pollError?.response?.data?.error || "";
        if (errorMessage.includes("Batch not found") || errorMessage.includes("expired")) {
          stopPolling();
          setIsGenerating(false);
          setBatchId(null);
          return;
        }

        if (Date.now() - lastProgressAtRef.current >= CAMPAIGN_PROGRESS_TIMEOUT_MS) {
          stopPolling();
          report("Error al obtener el progreso de la campaña.", true);
          setIsGenerating(false);
        }
      }
    };

    pollCampaignProgress();
    campaignPollingRef.current = setInterval(pollCampaignProgress, POLLING_INTERVAL_MS);

    return stopPolling;
  }, [isGenerating, batchId, configId, interceptorReady, report]);

  /**
   * Saves the communication style on the client config and asks the AI for the batch topics.
   * briefing: { configId, objective, tone, languageType, language, frequency, duration }
   */
  const start = useCallback(
    async (nextBriefing) => {
      const {
        configId: targetConfigId,
        objective = "",
        tone,
        languageType,
        language,
        frequency,
        duration,
      } = nextBriefing;

      if (!targetConfigId) {
        report("No se ha seleccionado una configuración de cliente.", true);
        return;
      }

      setIsStarting(true);
      try {
        await apiClient.patch(`content-generation/configs/${targetConfigId}/`, {
          language,
          tone,
          language_type: languageType,
        });

        const numPieces = frequency * duration;
        const { data: configDetails } = await apiClient.get(`content-generation/configs/${targetConfigId}/`);

        setStarted(true);
        setIsGenerating(true);
        setConfigId(targetConfigId);
        setBriefing({ ...nextBriefing, objective: objective.trim(), clientName: configDetails.client_name || "" });
        setPieceIds([]);
        setTopics([]);
        setIsEditingTopics(false);
        setBatchId(null);
        setExpectedPieces(numPieces);
        setProgress({ total: numPieces, completed: 0, failed: 0, pending: numPieces, status: "running" });

        const previewResponse = await apiClient.post("content-generation/pieces/preview_topics/", {
          client_name: configDetails.client_name || "",
          sector: configDetails.business_type || "",
          ...(configDetails.website && configDetails.website !== "No especificado" && { website: configDetails.website }),
          tone,
          language_type: languageType,
          additional_notes: objective.trim(),
          buyer_personas: (configDetails.buyer_personas || []).map((persona) => ({
            name: persona.name,
            age: persona.age || null,
            interests: persona.interests || "",
            pain_points: persona.pain_points || "",
            objectives: persona.objectives || "",
          })),
          num_pieces: numPieces,
        });
        setTopicsTaskId(previewResponse.data.task_id);
      } catch (error) {
        console.error("Error starting campaign generation:", error);
        report(
          error.response?.data?.error || error.response?.data?.detail || "Error al iniciar la generación de campaña.",
          true
        );
        setIsGenerating(false);
        setStarted(false);
      } finally {
        setIsStarting(false);
      }
    },
    [report]
  );

  /**
   * Single piece: saves the style on the config and creates one (non-campaign) piece
   * from the given topic. The panel follows its generation by polling the piece.
   * briefing: { configId, topic, initialPrompt, tone, languageType, language }
   */
  const startSingle = useCallback(
    async ({ configId: targetConfigId, topic = "", initialPrompt = "", tone, languageType, language }) => {
      if (!targetConfigId) {
        report("No se ha seleccionado una configuración de cliente.", true);
        return;
      }
      const title = topic.trim();
      if (!title) {
        report("Escribe la temática del contenido.", true);
        return;
      }

      setIsStarting(true);
      try {
        await apiClient.patch(`content-generation/configs/${targetConfigId}/`, {
          language,
          tone,
          language_type: languageType,
        });
        const { data } = await apiClient.post("content-generation/pieces/", {
          config: targetConfigId,
          title,
          initial_prompt: initialPrompt.trim(),
        });

        setStarted(true);
        setIsGenerating(true);
        setConfigId(targetConfigId);
        setBriefing({ topic: title, initialPrompt: initialPrompt.trim(), tone, languageType, language });
        setPieceIds([data.id]);
        setSingleTaskId(data.task_id || null);
        setExpectedPieces(1);
        setProgress({ total: 1, completed: 0, failed: 0, pending: 1, status: "running" });
      } catch (error) {
        console.error("Error creating content piece:", error);
        report(error.response?.data?.detail || error.response?.data?.error || "Error al crear el contenido.", true);
      } finally {
        setIsStarting(false);
      }
    },
    [report]
  );

  // Creates the pieces from the reviewed topics and starts the batch.
  const confirmTopics = useCallback(async () => {
    if (!configId) return;
    const normalizedTopics = topics.map((topic) => String(topic || "").trim()).filter(Boolean);
    if (!normalizedTopics.length) {
      report("Debes mantener al menos un topic.", true);
      return;
    }

    setIsConfirming(true);
    try {
      const { data } = await apiClient.post(
        "content-generation/pieces/confirm_campaign/",
        { config: configId, topics: normalizedTopics },
        { timeout: Math.max(30000, normalizedTopics.length * 30000) }
      );
      const { batch_id, pieces_created, content_piece_ids } = data;
      const totalPieces = pieces_created || normalizedTopics.length;
      setPieceIds(Array.isArray(content_piece_ids) ? content_piece_ids : []);
      setBatchId(batch_id);
      setExpectedPieces(totalPieces);
      setProgress({ total: totalPieces, completed: 0, failed: 0, pending: totalPieces, status: "running" });
      setIsEditingTopics(false);
      setTopics([]);
    } catch (confirmError) {
      console.error("Error confirming campaign:", confirmError);
      report(
        confirmError.response?.data?.detail || confirmError.response?.data?.error || "Error al confirmar la campaña.",
        true
      );
    } finally {
      setIsConfirming(false);
    }
  }, [configId, topics, report]);

  const cancel = useCallback(() => {
    setStarted(false);
    setIsStarting(false);
    setIsGenerating(false);
    setTopicsTaskId(null);
    setTopics([]);
    setIsEditingTopics(false);
    setIsConfirming(false);
    setBatchId(null);
    setExpectedPieces(0);
    setConfigId(null);
    setPieceIds([]);
    setSingleTaskId(null);
    setProgress(IDLE_PROGRESS);
  }, []);

  const setTopic = useCallback((index, value) => {
    setTopics((prev) => prev.map((topic, i) => (i === index ? value : topic)));
  }, []);
  const removeTopic = useCallback((index) => {
    setTopics((prev) => prev.filter((_, i) => i !== index));
  }, []);
  const addTopic = useCallback(() => setTopics((prev) => [...prev, ""]), []);

  let phase = "briefing";
  if (started || isStarting) {
    if (isEditingTopics) phase = "topics_review";
    else if (isStarting || topicsTaskId) phase = "topics_loading";
    else if (isGenerating) phase = "generating";
    else if (pieceIds.length || batchId) phase = "done";
  }

  return {
    phase,
    briefing,
    configId,
    isStarting,
    isGenerating,
    batchId,
    expectedPieces,
    progress,
    topics,
    setTopic,
    removeTopic,
    addTopic,
    isConfirming,
    pieceIds,
    start,
    startSingle,
    confirmTopics,
    cancel,
  };
}
